import type { Translation } from '../types.ts';
import type en from '../en/settings.ts';

export default {
    shared: {
        storedPlaceholder: 'Opgeslagen; typ om te vervangen',
    },
    tabs: {
        checkout: 'Afrekenen',
        invoices: 'Facturen',
        customer: 'Klanten',
        vehicleLookup: 'Kenteken opzoeken',
        vehicleStock: 'Voertuigvoorraad',
    },
    modules: {
        introSuper: 'Zet optionele functies aan voor deze website. Een module blijft inactief totdat al zijn afhankelijkheden ook aan staan.',
        intro: 'Modules die voor deze website aan staan. Neem contact met ons op om er meer toe te voegen.',
        partlyOnNote: 'Deels aan, hieronder zie je welke onderdelen.',
        partlyOn: 'Deels aan',
        alsoEnables: 'Als je dit aanzet, gaat ook {modules} aan.',
        requires: 'vereist {modules}',
        inactiveDeps: 'Inactief, zet eerst {modules} aan.',
        countOn: '{on} van {total} aan',
        categories: {
            content: 'Inhoud',
            customers: 'Klanten',
            webshop: 'Verkoop',
            pricing: 'Prijzen',
            delivery: 'Bezorging',
            shopMail: 'Mails aan klanten',
            accounting: 'Boekhouding',
            channels: 'Verkoopkanalen',
            marketing: 'Marketing',
            vehicles: 'Voertuigen',
            other: 'Overig',
        },
        names: {
            abandoned_cart: 'Verlaten-winkelwagenmails',
            blog: 'Blog',
            cart: 'Winkelwagen',
            catalog: 'Productcatalogus',
            catalog_discount: 'Catalogus- en staffelkortingen',
            checkout: 'Afrekenen',
            customer: 'Klantaccounts',
            delivery_slot: 'Bezorgmomenten',
            discount: 'Kortingen',
            faq: 'FAQ',
            giftcard: 'Cadeaubonnen',
            landingpages: 'Landingspagina\'s',
            newsletter: 'Nieuwsbrief',
            portfolio: 'Portfolio',
            review_request: 'Reviewverzoeken',
            seo: 'SEO',
            shipping: 'Verzending en vervoerders',
            tax: 'Btw-berekening',
            vehicle_lookup: 'Kenteken opzoeken',
            vehicle_stock: 'Voertuigvoorraad',
        },
        groups: {
            ecommerce: {
                label: 'Webshop',
                description: 'Productcatalogus, winkelwagen en afrekenen.',
            },
            discounts: {
                label: 'Kortingen',
                description: 'Kortingscodes bij het afrekenen, cadeaubonnen die in de winkel worden verkocht en automatische actie- of staffelprijzen in de catalogus.',
            },
        },
    },
    google: {
        analytics: {
            title: 'Google Analytics en Tag Manager',
            intro: 'Op elke pagina geladen, pas nadat de bezoeker het via de cookiemelding toestaat: Analytics heeft toestemming voor statistieken nodig, Tag Manager een van beide soorten, en Google\'s Consent Mode vertelt de tags welke is gegeven. De e-commerce-events van de winkel (productweergaven, winkelwagen, afrekenen, aankoop) gaan naar allebei.',
            ga4: 'GA4-metings-ID',
            gtm: 'Tag Manager-container-ID',
            oneOfTwo: 'Vul een van de twee in. Met Tag Manager stel je GA4 in de container in, niet hier, anders wordt elk bezoek dubbel geteld.',
        },
        server: {
            title: 'Aankopen vanaf de server verstuurd',
            intro: 'Een betaling die rondkomt nadat de klant al weg is (een bankoverschrijving die nog loopt, een gesloten tabblad) laat de klant nooit de bedankpagina zien, dus de tags zien de aankoop nooit. Met een API-secret stuurt de winkel die aankopen zelf naar GA4, tien minuten na de betaling, voor bezoekers die statistiekcookies hebben toegestaan. GA4 telt elke bestelling één keer, vanaf de pagina of vanaf de server. Vereist het GA4-metings-ID hierboven.',
            apiSecret: 'Measurement Protocol API-secret',
            apiSecretPlaceholder: 'GA4 Beheer, Gegevensstreams, je stream, Measurement Protocol API-secrets',
            removeSecret: 'Het opgeslagen secret verwijderen',
        },
        verification: {
            title: 'Siteverificatie',
            intro: 'Bewijst aan Google Search Console en Bing Webmaster Tools dat deze site van jou is. Kies daar de methode HTML-tag en plak de code, of de hele {tag}-tag. Hij wordt aan elke pagina toegevoegd; hij plaatst geen cookie, dus er is geen toestemming voor nodig.',
        },
        feed: {
            title: 'Productfeed voor Merchant Center',
            intro: 'Voeg in Google Merchant Center een feed toe (Producten, Producten toevoegen uit een bestand, geplande ophaalactie) met de URL voor elke taal waarin je verkoopt. De feed bevat elk gepubliceerd product met een afbeelding en een prijs, tegen de prijs die de winkel toont, met merk, barcode en verzending. Cadeaubonnen worden weggelaten.',
            moduleOff: 'De feed werkt zodra de Google-module aan staat.',
        },
        save: 'Google-instellingen opslaan',
    },
    invoices: {
        intro: 'Hoe facturen en creditnota\'s genummerd worden, wat erop staat, hoe ze eruitzien en of ze vanzelf aangemaakt en gemaild worden. De teksten, bankgegevens en vervaldatum worden bij het aanmaken op de factuur gezet, dus een wijziging hier verandert nooit een factuur die al is uitgegeven.',
        checkoutOff: 'Facturen beginnen zodra de afrekenmodule aan staat.',
        wholeDays: 'Vul een heel aantal dagen in, of laat het leeg.',
        wholeNumber: 'Vul een heel getal van 1 of hoger in.',
        numbering: {
            title: 'Nummering',
            format: 'Nummeropmaak',
            reset: 'Teller begint opnieuw',
            resets: {
                monthly: 'Elke maand',
                yearly: 'Elk jaar',
                never: 'Nooit',
            },
            tokens: 'Plaatshouders:',
            tokensHelp: 'YYYY en YY zijn het jaar, MM de maand, N het volgnummer (NNNN vult het aan tot vier cijfers). Letters, cijfers en . _ / - worden overgenomen zoals ze zijn.',
            nextNumber: 'Volgend volgnummer',
            nextNumberHint: 'Voor de huidige {period}. Het kan alleen omhoog: een nummer dat al is uitgegeven, wordt nooit opnieuw uitgegeven.',
            periods: {
                monthly: 'maand',
                yearly: 'jaar',
                never: 'reeks',
            },
            nextInvoice: 'De volgende factuur krijgt nummer',
            shared: 'Creditnota\'s worden in dezelfde reeks genummerd.',
        },
        payment: {
            title: 'Betaling',
            term: 'Betaaltermijn in dagen',
            termHint: 'Een factuur voor een bestelling die nog niet betaald is (rembours, of gefactureerd voordat het geld binnen is) krijgt een vervaldatum zoveel dagen na de factuurdatum. Leeg: geen vervaldatum.',
            holder: 'Ten name van',
            iban: 'IBAN',
            bic: 'BIC',
            bankHint: 'Komt op elke factuur, bij de betaalinstructie.',
        },
        texts: {
            title: 'Teksten op de factuur',
            intro: 'Boven de regels',
            payment: 'Betaalinstructie',
            paymentHint: 'Alleen op een factuur die nog niet betaald is. Leeg: met een betaaltermijn vraagt een standaardregel om het totaal vóór de vervaldatum te betalen onder vermelding van het factuurnummer.',
            footer: 'Onderaan',
            placeholders: 'Plaatshouders:',
            fallback: 'Een tekst die in een taal leeg is, gebruikt die van {locale}.',
        },
        look: {
            title: 'Opmaak',
            logo: 'Logo',
            noLogo: 'Geen logo',
            chooseLogo: 'Logo kiezen',
            changeLogo: 'Wijzigen',
            removeLogo: 'Verwijderen',
            logoTypes: 'Een PNG, JPEG of GIF uit de mediabibliotheek.',
            logoOtherWebsite: 'Schakel naar deze website om het logo uit de mediabibliotheek te kiezen.',
            logoWrongType: 'Het logo moet een PNG-, JPEG- of GIF-afbeelding zijn.',
            logoSize: 'Logogrootte',
            sizes: {
                small: 'Klein',
                medium: 'Middel',
                large: 'Groot',
            },
            accent: 'Accentkleur',
            accentHint: 'De titel, de lijnen onder de kopjes en het totaal.',
            font: 'Lettertype',
            fonts: {
                sans: 'Schreefloos',
                serif: 'Met schreef',
                mono: 'Vaste breedte',
            },
        },
        when: {
            title: 'Aanmaken en versturen',
            createOn: 'Factuur aanmaken',
            create: {
                manual: 'Met de hand, vanuit de bestelling',
                paid: 'Als de bestelling betaald is',
                shipped: 'Als de bestelling verzonden is',
            },
            createHint: 'Rembours telt als betaald zodra de betaling is vastgelegd.',
            creditNoteOn: 'Creditnota aanmaken',
            credit: {
                manual: 'Met de hand, vanuit de bestelling',
                refunded: 'Als de bestelling terugbetaald is',
            },
            sendOn: 'Naar de klant mailen',
            send: {
                manual: 'Met de hand, vanuit de bestelling',
                created: 'Zodra hij automatisch is aangemaakt',
                shipped: 'Als de bestelling verzonden is',
            },
            sendHint: 'Een factuur die met de hand is aangemaakt, wacht nog steeds op Factuur versturen. Een automatisch aangemaakte creditnota wordt meteen gemaild als versturen automatisch gaat. bol.com-bestellingen worden nooit gemaild: bol informeert zijn kopers.',
            fromNow: 'Alleen bestellingen die vanaf nu betaald, verzonden of terugbetaald worden, worden meegenomen; eerdere bestellingen blijven zoals ze zijn.',
            noMailFrom: 'Deze website heeft geen afzenderadres, dus facturen kunnen niet gemaild worden. Stel er eerst een in bij de afrekeninstellingen.',
        },
        preview: 'Voorbeeld',
        previewIn: 'Voorbeeld in',
        save: 'Factuurinstellingen opslaan',
    },
    meta: {
        intro: 'Op elke pagina geladen, pas nadat de bezoeker marketingcookies toestaat: PageView overal, Purchase op de bedankpagina. Met het toegangstoken wordt elke betaalde bestelling die met marketingtoestemming is geplaatst ook vanaf de server verstuurd (Conversions API), met hetzelfde event-ID, zodat Meta hem één keer telt.',
        moduleOff: 'Hij werkt zodra de Meta Pixel-module aan staat.',
        pixelId: 'Pixel-ID',
        token: 'Toegangstoken voor de Conversions API',
        tokenPlaceholder: 'Uit Events Manager, Instellingen, Conversions API',
        removeToken: 'Het opgeslagen token verwijderen',
        testCode: 'Testeventcode',
        testCodeNote: 'Zolang deze is ingevuld, gaan server-events alleen naar het tabblad Test events in Events Manager. Maak het veld leeg om live te gaan.',
        sendTest: 'Testevent versturen',
        testReceived: 'Meta heeft geen testevents ontvangen. | Meta heeft {n} testevent ontvangen. Je ziet het in Events Manager onder Test events. | Meta heeft {n} testevents ontvangen. Je ziet ze in Events Manager onder Test events.',
        save: 'Meta-instellingen opslaan',
    },
} satisfies Translation<typeof en>;
